import { useState, useEffect, useCallback } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';
import type { RealCall } from '../types.js';
import { CallCard } from './CallCard.js';
import { FilterBar } from './FilterBar.js';
import type { FilterState } from './FilterBar.js';
import { CallDetail } from './CallDetail.js';

const PAGE_SIZE = 8;

function SkeletonCard() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 animate-pulse">
      <div className="flex gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-800 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-slate-800 rounded w-32" />
          <div className="h-3 bg-slate-800 rounded w-48" />
          <div className="h-3 bg-slate-800 rounded w-full" />
        </div>
      </div>
    </div>
  );
}

export function CallFeed() {
  const [filters, setFilters] = useState<FilterState>({ tier: 'all', intent: 'all', buyer: 'all', state: 'all', search: '' });
  const [page, setPage] = useState(1);
  const [calls, setCalls] = useState<RealCall[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const load = useCallback((f: FilterState, pg: number) => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    setLoading(true);
    trpc.calls.list.query({
      tenantId: user.tenantId,
      tier: f.tier !== 'all' ? f.tier : undefined,
      intent: f.intent !== 'all' ? f.intent : undefined,
      buyerId: f.buyer !== 'all' ? f.buyer : undefined,
      state: f.state !== 'all' ? f.state : undefined,
      page: pg,
      pageSize: PAGE_SIZE,
    })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((result: any) => {
        setCalls(result.calls ?? []);
        setTotal(result.total ?? 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filters, page);
  }, [filters, page, load]);

  function handleFilterChange(f: FilterState) {
    setFilters(f);
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <FilterBar onFilterChange={handleFilterChange} />

      {/* Results summary */}
      <div className="flex items-center justify-between text-xs text-slate-500 px-1">
        <span>{loading ? 'Loading…' : `${total} call${total !== 1 ? 's' : ''} found`}</span>
        <span>Page {page} of {totalPages}</span>
      </div>

      {/* Call list */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: PAGE_SIZE }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : calls.length > 0 ? (
        <div className="space-y-3">
          {calls.map(call => (
            <CallCard key={call.id} call={call} onDetail={setSelectedCallId} />
          ))}
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
          <p className="text-3xl mb-3">🔍</p>
          <p className="text-slate-400 text-sm">No calls found.</p>
          {filters.tier !== 'all' || filters.intent !== 'all' || filters.buyer !== 'all' || filters.state !== 'all' ? (
            <button
              onClick={() => handleFilterChange({ tier: 'all', intent: 'all', buyer: 'all', state: 'all', search: '' })}
              className="mt-3 text-xs text-blue-400 hover:text-blue-300 underline"
            >
              Clear filters
            </button>
          ) : (
            <p className="text-slate-600 text-xs mt-1">No calls have been ingested yet.</p>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-xs rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ← Prev
          </button>

          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              onClick={() => setPage(n)}
              className={`w-8 h-8 text-xs rounded-lg transition-colors ${
                n === page
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {n}
            </button>
          ))}

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-xs rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      )}

      {/* Detail modal */}
      {selectedCallId && (
        <CallDetail callId={selectedCallId} onClose={() => setSelectedCallId(null)} />
      )}
    </div>
  );
}
